import { SetterOrUpdater } from "recoil";

import { COURSES_CALENDAR_LAST_HOUR_INDEX } from "../../constants/courses-calendar.constants";
import { SelectionEnum } from "../../enums";
import {
  CoursesCalendarSlotSelectionId,
  DateYMDString,
  ICoursesCalendarSlotDto,
} from "../../models";
import { COURSES_CALENDAR_TIME_PARSE_RADIX } from "./courses-calendar.constants";

const isCoursesCalendarSlotSelectionId = (
  value: unknown,
): value is CoursesCalendarSlotSelectionId => typeof value === "string";

const isCoursesCalendarSlotSelectionIdArray = (
  value: unknown,
): value is CoursesCalendarSlotSelectionId[] =>
  Array.isArray(value) && value.every(isCoursesCalendarSlotSelectionId);

export const normalizeCoursesCalendarSelectedSlotIds = (
  value: unknown,
): Set<CoursesCalendarSlotSelectionId> => {
  if (value instanceof Set) {
    return new Set([...value].filter(isCoursesCalendarSlotSelectionId));
  }
  if (isCoursesCalendarSlotSelectionIdArray(value)) {
    return new Set(value);
  }
  return new Set();
};

export const hourFromCoursesCalendarTimeLabel = (
  timeLabel: string,
): number | null => {
  const part = timeLabel.split(":")[0];
  if (part === undefined) {
    return null;
  }
  const hour = Number.parseInt(part, COURSES_CALENDAR_TIME_PARSE_RADIX);
  if (
    Number.isNaN(hour) ||
    hour < 0 ||
    hour > COURSES_CALENDAR_LAST_HOUR_INDEX
  ) {
    return null;
  }
  return hour;
};

export const collectCoursesCalendarSlotIds = (
  slots: readonly ICoursesCalendarSlotDto[],
): Set<CoursesCalendarSlotSelectionId> => new Set(slots.map(slot => slot.id));

export const collectCoursesCalendarSlotIdsByHour = ({
  slots,
  hourIndex,
}: {
  slots: readonly ICoursesCalendarSlotDto[];
  hourIndex: number;
}): Set<CoursesCalendarSlotSelectionId> => {
  const ids = new Set<CoursesCalendarSlotSelectionId>();
  for (const slot of slots) {
    const hour = hourFromCoursesCalendarTimeLabel(slot.timeLabel);
    if (hour === hourIndex) {
      ids.add(slot.id);
    }
  }
  return ids;
};

export const collectCoursesCalendarSlotIdsByDate = ({
  slots,
  dateKey,
}: {
  slots: readonly ICoursesCalendarSlotDto[];
  dateKey: DateYMDString;
}): Set<CoursesCalendarSlotSelectionId> => {
  const ids = new Set<CoursesCalendarSlotSelectionId>();
  for (const slot of slots) {
    if (String(slot.dateKey) === String(dateKey)) {
      ids.add(slot.id);
    }
  }
  return ids;
};

const selectCoursesCalendarSlots = ({
  setter,
  slotIds,
}: {
  setter: SetterOrUpdater<Set<CoursesCalendarSlotSelectionId>>;
  slotIds: Set<CoursesCalendarSlotSelectionId>;
}): void => {
  setter(
    prevValue =>
      new Set([
        ...normalizeCoursesCalendarSelectedSlotIds(prevValue),
        ...slotIds,
      ]),
  );
};

const deselectCoursesCalendarSlots = ({
  setter,
  slotIds,
}: {
  setter: SetterOrUpdater<Set<CoursesCalendarSlotSelectionId>>;
  slotIds: Set<CoursesCalendarSlotSelectionId>;
}): void => {
  setter(prevValue => {
    const next = new Set(normalizeCoursesCalendarSelectedSlotIds(prevValue));
    slotIds.forEach(id => {
      next.delete(id);
    });
    return next;
  });
};

export const setCheckedHandlerCoursesCalendarSlots: Record<
  SelectionEnum,
  (args: {
    setter: SetterOrUpdater<Set<CoursesCalendarSlotSelectionId>>;
    slotIds: Set<CoursesCalendarSlotSelectionId>;
  }) => void
> = {
  [SelectionEnum.All]: selectCoursesCalendarSlots,
  [SelectionEnum.Some]: selectCoursesCalendarSlots,
  [SelectionEnum.None]: deselectCoursesCalendarSlots,
};

export const getCoursesCalendarSelectionState = (
  selectedIds: Set<CoursesCalendarSlotSelectionId>,
  targetIds: Set<CoursesCalendarSlotSelectionId>,
): SelectionEnum => {
  if (!targetIds.size || !selectedIds.size) {
    return SelectionEnum.None;
  }

  let isAllSelected = true;
  let isSomeSelected = false;

  targetIds.forEach(id => {
    if (selectedIds.has(id)) {
      isSomeSelected = true;
    } else {
      isAllSelected = false;
    }
  });

  if (isAllSelected) {
    return SelectionEnum.All;
  }
  return isSomeSelected ? SelectionEnum.Some : SelectionEnum.None;
};
